/**
 * Today's arithmetic, kept pure: which attention items are urgent, and a one-line morning brief
 * worked out from the data itself ("3 due to calve this week · 1 booster due · 2 notes need a look").
 * No model call: the brief must be instant and always true to what's on the page.
 */
import type { TaskDTO, TodayDTO, TodayItem } from "../../contract";
import { plural, t } from "../lib/i18n";

export type Urgency = "now" | "today" | "soon" | "later";

export interface TodayGroups {
  now: { items: TodayItem[]; tasks: TaskDTO[] };
  today: { items: TodayItem[]; tasks: TaskDTO[] };
  soon: { items: TodayItem[]; tasks: TaskDTO[] };
  later: { items: TodayItem[]; tasks: TaskDTO[] };
}

const day = (v: string | null | undefined) => (v ? v.slice(0, 10) : null);
const addDays = (date: string, n: number) => new Date(Date.parse(`${date}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Alerts and anything overdue need you now; then today; then the next week; then the rest. */
export function urgencyOf(item: Pick<TodayItem, "kind" | "due_at">, today: string): Urgency {
  const due = day(item.due_at);
  if (item.kind === "alert" || (due && due < today)) return "now";
  if (due === today || (!due && item.kind === "due")) return "today";
  if (due && due <= addDays(today, 7)) return "soon";
  return "later";
}

export function groupToday(data: Pick<TodayDTO, "items" | "tasks" | "date">): TodayGroups {
  const groups: TodayGroups = { now: { items: [], tasks: [] }, today: { items: [], tasks: [] }, soon: { items: [], tasks: [] }, later: { items: [], tasks: [] } };
  for (const item of data.items) groups[urgencyOf(item, data.date)].items.push(item);
  for (const task of data.tasks) {
    const due = day(task.due_at);
    groups[due && due < data.date ? "now" : "today"].tasks.push(task);
  }
  return groups;
}

/**
 * One short line: the most important module items in their own words, then notes to check and
 * tasks. At most four parts. Empty when nothing is pressing.
 */
export function morningBrief(data: Pick<TodayDTO, "items" | "tasks" | "review" | "date">, translate: (text: string, values?: Record<string, unknown>) => string = t): string[] {
  const parts: string[] = [];
  const pressing = data.items
    .filter((i) => i.kind === "alert" || i.kind === "due")
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 2);
  for (const item of pressing) parts.push(translate(item.title.text, item.title.values));
  if (data.review.count > 0) parts.push(plural(data.review.count, "{{count}} note needs a look", "{{count}} notes need a look"));
  const overdue = data.tasks.filter((x) => day(x.due_at) && day(x.due_at)! < data.date).length;
  const dueToday = data.tasks.length - overdue;
  if (overdue) parts.push(plural(overdue, "{{count}} task overdue", "{{count}} tasks overdue"));
  if (dueToday) parts.push(plural(dueToday, "{{count}} task due today", "{{count}} tasks due today"));
  return parts.slice(0, 4);
}
